import {
  parseCreateSlidesDialogMessage,
  serialiseCreateSlidesMessage,
  type CreateSlidesPaneToDialog,
} from "../service/createSlidesDialogChannel";
import type { OutlineResponse } from "../service/templatesApi";
import type { CreateSlideConfig, TemplatePlan } from "./types";

/**
 * Office Dialog window size, as a PERCENTAGE of the screen (the Office Dialog
 * API only accepts percentages and can't resize per step). Deliberately large
 * so the window acts as the modal SCRIM: the CSS floats a compact centred card
 * with a dark backdrop around it, and Office centres the window on screen.
 */
const DIALOG_SIZE = { height: 80, width: 82 } as const;

/** Relative path of the dialog page served from the add-in origin. */
const DIALOG_PAGE = "/create-slides-dialog.html";

/** Whether the Office Dialog API is available (i.e. running inside a host). */
export function canUseDialog(): boolean {
  return (
    typeof Office !== "undefined" &&
    typeof Office.context?.ui?.displayDialogAsync === "function"
  );
}

/** The directory SAS per template library, as the pane mints them. */
export interface TemplateCatalogSas {
  readonly libraries: readonly { readonly sasUrl: string; readonly manifestBlobPath: string }[];
}

/**
 * What the wizard needs the PANE to do while the dialog is still open. Both
 * are things the dialog window physically can't do for itself: the outline
 * call needs a bearer token, and minting the library SAS needs the
 * authorised HttpClient. See service/createSlidesDialogChannel.ts.
 *
 * A handler that rejects is reported into the dialog as an error state (with
 * a Retry) rather than tearing the window down — the user hasn't lost their
 * wizard input.
 */
export interface CreateSlidesDialogHandlers {
  readonly requestOutline: (config: CreateSlideConfig) => Promise<OutlineResponse>;
  readonly requestTemplateCatalog: () => Promise<TemplateCatalogSas>;
}

/** What the user finished the wizard with. `plan` is set only for the
 *  templates path — freehand generation runs off `config` alone. */
export interface CreateSlidesDialogResult {
  readonly config: CreateSlideConfig;
  readonly plan?: TemplatePlan;
}

/** Message text for a host that can't push messages into a dialog window. */
const NO_CHILD_CHANNEL_MESSAGE =
  "This PowerPoint version can't send data into the wizard window (needs DialogApi 1.2+), so slide templates aren't available. Choose AI design instead.";

type DialogHandle = Office.Dialog & { messageChild?: (message: string) => void };

/** Whether this host can push messages INTO the dialog window (DialogApi 1.2+). */
function canMessageChild(dialog: DialogHandle): boolean {
  return typeof dialog.messageChild === "function";
}

function replyToDialog(dialog: DialogHandle, message: CreateSlidesPaneToDialog): void {
  try {
    dialog.messageChild?.(serialiseCreateSlidesMessage(message));
  } catch (error) {
    // The window can be mid-teardown when a handler resolves; messageChild
    // throws rather than no-opping in that case. Nothing to recover — the
    // dialog is gone, so the reply has nowhere to land.
    console.warn("[create-slide] couldn't reply to the wizard window:", error);
  }
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Opens the "Create slides" wizard in an Office Dialog (a modal window
 * floating over PowerPoint) and keeps it open for the whole wizard.
 *
 * The dialog collects the wizard input and — on the templates path — the
 * per-slide layout picks, delegating the two authorised steps back here via
 * `handlers` (see `CreateSlidesDialogHandlers`). Generation itself always
 * runs in the TASK PANE after this resolves, so the user watches per-slide
 * progress and sees each slide inserted live.
 *
 * File objects can't cross the messageParent boundary, so `documents[].file`
 * is dropped from the returned config (only its name/chars survive). That's
 * fine: generation sends document input via blob storage, not the raw File.
 *
 * Resolves with the collected config (+ plan), or null if the user
 * cancels/closes. Rejects (with a user-facing message) if the dialog window
 * cannot be opened — so the caller can distinguish "user cancelled" (null)
 * from "failed to open".
 */
export function openCreateSlidesDialog(
  handlers: CreateSlidesDialogHandlers,
): Promise<CreateSlidesDialogResult | null> {
  return new Promise((resolve, reject) => {
    if (!canUseDialog()) {
      resolve(null);
      return;
    }
    const url = `${globalThis.location.origin}${DIALOG_PAGE}`;
    Office.context.ui.displayDialogAsync(url, { ...DIALOG_SIZE }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        // Log the technical detail (Office error code/message) for diagnosis,
        // and reject with a message safe to show the user.
        const err = asyncResult.error;
        console.error(
          `[create-slide] Office failed to open the Create Slides dialog (code ${err?.code ?? "?"}): ${err?.message ?? "unknown error"}`,
        );
        reject(new Error("Couldn't open the Create Slides window. Please try again."));
        return;
      }
      const dialog = asyncResult.value as DialogHandle;
      let settled = false;
      const settle = (value: CreateSlidesDialogResult | null) => {
        if (settled) return;
        settled = true;
        try {
          dialog.close();
        } catch {
          /* already closed */
        }
        resolve(value);
      };

      const serveOutline = (config: CreateSlideConfig) => {
        handlers
          .requestOutline(config)
          .then((outline) => replyToDialog(dialog, { type: "outline-ready", outline }))
          .catch((error: unknown) => {
            console.error("[create-slide] outline request failed:", error);
            replyToDialog(dialog, {
              type: "outline-error",
              message: errorMessage(error, "Couldn't generate the slide outline."),
            });
          });
      };

      const serveCatalog = () => {
        handlers
          .requestTemplateCatalog()
          .then((sas) => replyToDialog(dialog, { type: "catalog-ready", libraries: sas.libraries }))
          .catch((error: unknown) => {
            console.error("[create-slide] template catalog request failed:", error);
            replyToDialog(dialog, {
              type: "catalog-error",
              message: errorMessage(error, "Couldn't load the slide template library."),
            });
          });
      };

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
        const message = parseCreateSlidesDialogMessage(arg);
        if (!message) {
          // Malformed message from the dialog (bad JSON / unexpected shape).
          // Unlike the old one-shot channel this must NOT settle the promise:
          // a single unrecognised frame mid-wizard would close a window the
          // user is still working in.
          console.error("[create-slide] unrecognised message from the wizard window; ignoring:", arg);
          return;
        }
        switch (message.type) {
          case "outline-request":
            if (!canMessageChild(dialog)) {
              // Nothing can reach the window, so it would wait forever. The
              // dialog can't be told either — close it and surface the reason
              // in the pane instead of leaving a dead spinner on screen.
              console.error("[create-slide] host lacks Dialog.messageChild; templates path unavailable");
              settled = true;
              try {
                dialog.close();
              } catch {
                /* already closed */
              }
              reject(new Error(NO_CHILD_CHANNEL_MESSAGE));
              return;
            }
            serveOutline(message.config);
            return;
          case "catalog-request":
            if (!canMessageChild(dialog)) return;
            serveCatalog();
            return;
          case "generate":
            settle(message.plan ? { config: message.config, plan: message.plan } : { config: message.config });
            return;
          case "cancel":
            settle(null);
            return;
        }
      });
      // User closed the dialog (X / host close) — resolve so we don't hang.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => settle(null));
    });
  });
}
